import { useCallback, useEffect, useRef } from 'react';

import {
  DoBoxBacklogBucketKind,
  DoBoxSpecialBucketKey,
  InboxEntry,
  InboxEntryFilters,
  InboxEntrySort,
} from '@this+that/models';
import log from 'loglevel';

import {
  type DoBoxBucketLimitInput,
  useGetInitialDoBoxEntries,
  useGetPaginatedInboxEntries,
  useGetPaginatedInboxEntriesBySubgroup,
} from '../../services/inboxEntries.service';
import { useInboxEntriesStore } from '../../store';
import { BucketKey } from '../../store/Inbox/InboxEntries/types';
import { indexedDBAdapter } from '../../store/utils/indexedDBAdapter';
import { shouldSuppressByTombstone } from '../../store/utils/tombstones';
import { isAbortError } from '../../utils/isAbortError';

interface FetchInboxEntriesParams {
  taskGroupId?: string | null;
  limit: number;
  filters?: InboxEntryFilters;
  sort?: InboxEntrySort;
}

interface FetchInitialDoBoxEntriesParams {
  subgroupIds: string[];
  limitPerBucket: number;
  bucketLimits?: DoBoxBucketLimitInput[];
  merge?: boolean;
}

const isStaleRequest = (requestId: string | null | undefined): boolean =>
  useInboxEntriesStore.getState().taskManager.activeTaskGroup.taskGroupId !==
  requestId;

// A per-bucket counter, so two concurrent "Load more" clicks on two DIFFERENT
// subgroups don't supersede each other's own in-flight request (see
// isSupersededBucketRequest below).
const latestRequestIdByBucket: Record<BucketKey, number> = {};

const nextBucketRequestId = (bucketKey: BucketKey): number => {
  const nextId = (latestRequestIdByBucket[bucketKey] ?? 0) + 1;
  latestRequestIdByBucket[bucketKey] = nextId;
  return nextId;
};

const isSupersededBucketRequest = (
  bucketKey: BucketKey,
  requestId: number,
): boolean => requestId !== latestRequestIdByBucket[bucketKey];

const BACKLOG_BUCKET_KIND_BY_SPECIAL_KEY: Partial<
  Record<BucketKey, DoBoxBacklogBucketKind>
> = {
  [DoBoxSpecialBucketKey.BACKLOG]: DoBoxBacklogBucketKind.BACKLOG,
  [DoBoxSpecialBucketKey.SUGGESTIONS]: DoBoxBacklogBucketKind.SUGGESTIONS,
  [DoBoxSpecialBucketKey.APPROVALS]: DoBoxBacklogBucketKind.APPROVALS,
};

let latestInitialFetchRequestId = 0;

const isSupersededInitialFetchRequest = (requestId: number): boolean =>
  requestId !== latestInitialFetchRequestId;

let initialFetchController: AbortController | null = null;
const bucketFetchControllers: Record<BucketKey, AbortController> = {};

const startInitialFetchController = (): AbortController => {
  initialFetchController?.abort();
  initialFetchController = new AbortController();
  return initialFetchController;
};

const startBucketFetchController = (bucketKey: BucketKey): AbortController => {
  bucketFetchControllers[bucketKey]?.abort();
  const controller = new AbortController();
  bucketFetchControllers[bucketKey] = controller;
  return controller;
};

const abortAllFetchControllers = (): void => {
  initialFetchController?.abort();
  Object.values(bucketFetchControllers).forEach((controller) =>
    controller.abort(),
  );
  Object.keys(bucketFetchControllers).forEach((key) => {
    delete bucketFetchControllers[key as BucketKey];
  });
  Object.keys(latestRequestIdByBucket).forEach((key) => {
    delete latestRequestIdByBucket[key as BucketKey];
  });
};

const filterTombstonedEntries = async (
  entries: InboxEntry[],
): Promise<InboxEntry[]> => {
  const suppressFlags = await Promise.all(
    entries.map(async (entry) => {
      const deletedItemRecord = await indexedDBAdapter.getDeletedItem(entry.id);
      return shouldSuppressByTombstone(deletedItemRecord, entry.version);
    }),
  );

  return entries.filter((_entry, index) => !suppressFlags[index]);
};

export const useFetchInboxEntries = ({
  taskGroupId,
  limit,
  filters,
  sort,
}: FetchInboxEntriesParams) => {
  const { getPaginatedInboxEntries } = useGetPaginatedInboxEntries();
  const { getPaginatedInboxEntriesBySubgroup } =
    useGetPaginatedInboxEntriesBySubgroup();
  const { getInitialDoBoxEntries } = useGetInitialDoBoxEntries();

  const setLoading = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.setLoading,
  );
  const setBucketLoading = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.setBucketLoading,
  );
  const saveInboxEntries = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.saveInboxEntries,
  );
  const saveInitialDoBoxBuckets = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.saveInitialDoBoxBuckets,
  );
  const bucketPagination = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.bucketPagination,
  );
  const activeTaskGroupId = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.taskGroupId,
  );

  useEffect(() => {
    return () => {
      abortAllFetchControllers();
    };
  }, [activeTaskGroupId]);

  // Assigned during render (not an effect) so it's current the instant a request is issued —
  // an effect-based update would depend on this hook's call order relative to the fetch effect in DoBox.tsx.
  const latestFilterSortRef = useRef({ filters, sort });
  latestFilterSortRef.current = { filters, sort };

  const handleFetchInitial = useCallback(
    async ({
      subgroupIds,
      limitPerBucket,
      bucketLimits,
      merge,
    }: FetchInitialDoBoxEntriesParams) => {
      const requestTaskGroupId = taskGroupId;
      if (isStaleRequest(requestTaskGroupId)) return;
      const requestId = ++latestInitialFetchRequestId;
      const requestFilters = filters;
      const requestSort = sort;
      const controller = startInitialFetchController();

      setLoading(true);
      try {
        if (!taskGroupId) {
          throw new Error('Task group ID is required');
        }

        const bucketResults = await getInitialDoBoxEntries({
          taskGroupId,
          subgroupIds,
          limitPerBucket,
          filters,
          sort,
          bucketLimits,
          signal: controller.signal,
        });

        const filteredBucketResults = await Promise.all(
          bucketResults.map(async (bucket) => ({
            ...bucket,
            inboxEntries: await filterTombstonedEntries(bucket.inboxEntries),
          })),
        );

        if (
          isStaleRequest(requestTaskGroupId) ||
          isSupersededInitialFetchRequest(requestId) ||
          requestFilters !== latestFilterSortRef.current.filters ||
          requestSort !== latestFilterSortRef.current.sort
        )
          return;

        saveInitialDoBoxBuckets(filteredBucketResults, merge);
      } catch (error) {
        if (isAbortError(error)) return;
        log.error('Error fetching initial DoBox entries:', error);
      } finally {
        // Always clear the loading flag even when the response is discarded above, or the task group gets stuck spinning forever.
        if (
          !isStaleRequest(requestTaskGroupId) &&
          !isSupersededInitialFetchRequest(requestId)
        ) {
          setLoading(false);
        }
      }
    },
    [
      getInitialDoBoxEntries,
      filters,
      sort,
      taskGroupId,
      saveInitialDoBoxBuckets,
      setLoading,
    ],
  );

  const handleLoadMoreBucket = useCallback(
    async (bucketKey: BucketKey, limitOverride?: number) => {
      const bucketStartKey = bucketPagination[bucketKey]?.startKey;
      if (!bucketStartKey) {
        return;
      }

      const requestTaskGroupId = taskGroupId;
      if (isStaleRequest(requestTaskGroupId)) return;
      const requestId = nextBucketRequestId(bucketKey);
      const requestFilters = filters;
      const requestSort = sort;
      const bucketLimit = limitOverride ?? limit;
      const controller = startBucketFetchController(bucketKey);

      setBucketLoading(bucketKey, true);
      try {
        if (!taskGroupId) {
          throw new Error('Task group ID is required');
        }

        const backlogBucketKind = BACKLOG_BUCKET_KIND_BY_SPECIAL_KEY[bucketKey];

        const { inboxEntries, exclusiveStartKey: newExclusiveStartKey } =
          backlogBucketKind
            ? await getPaginatedInboxEntries({
                taskGroupId,
                limit: bucketLimit,
                exclusiveStartKey: bucketStartKey,
                filters: {
                  ...filters,
                  showNoTaskSubgroup: true,
                  backlogBucketKind,
                },
                sort,
                signal: controller.signal,
              })
            : await getPaginatedInboxEntriesBySubgroup({
                taskGroupId,
                taskSubgroupId: bucketKey,
                limit: bucketLimit,
                exclusiveStartKey: bucketStartKey,
                filters,
                sort,
                signal: controller.signal,
              });

        const filteredInboxEntries =
          await filterTombstonedEntries(inboxEntries);

        if (
          isStaleRequest(requestTaskGroupId) ||
          isSupersededBucketRequest(bucketKey, requestId) ||
          requestFilters !== latestFilterSortRef.current.filters ||
          requestSort !== latestFilterSortRef.current.sort
        )
          return;

        // DDB's LastEvaluatedKey means "the scan stopped here", not "more items exist" —
        // an empty page with a cursor would show a "Load more" that always returns nothing.
        const safeExclusiveStartKey =
          inboxEntries.length === 0 ? null : newExclusiveStartKey;

        saveInboxEntries(
          filteredInboxEntries,
          safeExclusiveStartKey,
          true,
          bucketKey,
        );
      } catch (error) {
        if (isAbortError(error)) return;
        log.error('Error loading more bucket inbox entries:', error);
      } finally {
        // Always clear the loading flag even when the response is discarded above, or the bucket gets stuck spinning forever.
        if (
          !isStaleRequest(requestTaskGroupId) &&
          !isSupersededBucketRequest(bucketKey, requestId)
        ) {
          setBucketLoading(bucketKey, false);
        }
      }
    },
    [
      getPaginatedInboxEntries,
      getPaginatedInboxEntriesBySubgroup,
      limit,
      bucketPagination,
      filters,
      sort,
      taskGroupId,
      saveInboxEntries,
      setBucketLoading,
    ],
  );

  return {
    handleFetchInitial,
    handleLoadMoreBucket,
  };
};
